export const ANALYTICS_CONSENT_COOKIE = "afterform_analytics_consent";
export const ANALYTICS_CONSENT_EPOCH_COOKIE =
  "afterform_analytics_consent_epoch";
export const ANALYTICS_CONSENT_SEEN_EPOCH_KEY =
  "afterform_analytics_consent_seen_epoch";
export const ANALYTICS_CONSENT_EVENT = "afterform:analytics-consent";
const ANALYTICS_CONSENT_SYNC_KEY = "afterform_analytics_consent_sync";
const ANALYTICS_CONSENT_POLL_INTERVAL_MS = 1_000;

export type AnalyticsConsent = "accepted" | "rejected";
export interface AnalyticsConsentSnapshot {
  choice: AnalyticsConsent | null;
  epoch: string | null;
}
type AnalyticsConsentObserver = (
  choice: AnalyticsConsent | null,
  epoch: string | null,
) => void;

const DATAFAST_COOKIE_NAMES = [
  "datafast_visitor_id",
  "datafast_visitor_first_seen_at",
  "datafast_visitor_session_count",
  "datafast_session_id",
  "datafast_session_start",
] as const;

const DATAFAST_STORAGE_NAMES = [
  ...DATAFAST_COOKIE_NAMES,
  "datafast_event_queue",
] as const;
const consentObservers = new Set<AnalyticsConsentObserver>();
let lastObservedConsent: AnalyticsConsentSnapshot | null = null;
let consentPoll: number | null = null;
const UUID_V4 =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function cookieValue(name: string): string | null {
  if (typeof document === "undefined") return null;
  const prefix = `${name}=`;
  for (const part of document.cookie.split(";")) {
    const candidate = part.trim();
    if (candidate.startsWith(prefix)) {
      try {
        return decodeURIComponent(candidate.slice(prefix.length));
      } catch {
        return null;
      }
    }
  }
  return null;
}

function sharedCookieDomain(configuredDomain: string): string | null {
  if (typeof window === "undefined") return null;
  const domain = configuredDomain.trim().replace(/^\./, "").toLowerCase();
  const hostname = window.location.hostname.toLowerCase();
  if (
    !domain ||
    hostname === "localhost" ||
    hostname.startsWith("127.") ||
    hostname === "::1"
  ) {
    return null;
  }
  return hostname === domain || hostname.endsWith(`.${domain}`) ? domain : null;
}

function writeCookie(
  name: string,
  value: string,
  maxAge: number,
  configuredDomain: string,
): void {
  if (typeof document === "undefined" || typeof window === "undefined") return;
  const domain = sharedCookieDomain(configuredDomain);
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  const domainPart = domain ? `; Domain=.${domain}` : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}${domainPart}`;
}

function expireCookieEverywhere(name: string, configuredDomain: string): void {
  if (typeof document === "undefined" || typeof window === "undefined") return;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
  const domain = sharedCookieDomain(configuredDomain);
  if (domain) {
    document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax${secure}; Domain=.${domain}`;
  }
}

function createConsentEpoch(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (token) => {
    const random = Math.floor(Math.random() * 16);
    const value = token === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

export function readAnalyticsConsentSnapshot(): AnalyticsConsentSnapshot {
  const rawChoice = cookieValue(ANALYTICS_CONSENT_COOKIE);
  const rawEpoch = cookieValue(ANALYTICS_CONSENT_EPOCH_COOKIE);
  const epoch = rawEpoch && UUID_V4.test(rawEpoch) ? rawEpoch : null;
  const choice =
    epoch && (rawChoice === "accepted" || rawChoice === "rejected")
      ? rawChoice
      : null;
  return { choice, epoch };
}

export function readAnalyticsConsent(): AnalyticsConsent | null {
  return readAnalyticsConsentSnapshot().choice;
}

export function readSeenAnalyticsConsentEpoch(): string | null {
  try {
    const epoch = localStorage.getItem(ANALYTICS_CONSENT_SEEN_EPOCH_KEY);
    return epoch && UUID_V4.test(epoch) ? epoch : null;
  } catch {
    return null;
  }
}

export function writeSeenAnalyticsConsentEpoch(epoch: string | null): void {
  try {
    if (epoch) localStorage.setItem(ANALYTICS_CONSENT_SEEN_EPOCH_KEY, epoch);
    else localStorage.removeItem(ANALYTICS_CONSENT_SEEN_EPOCH_KEY);
  } catch {
    // A missing seen epoch deliberately forces a purge on the next startup.
  }
}

export function writeAnalyticsConsent(
  choice: AnalyticsConsent,
  configuredDomain: string,
): string {
  const epoch = createConsentEpoch();
  if (sharedCookieDomain(configuredDomain)) {
    // Legacy host-only values must not shadow the root-domain authority.
    if (typeof document !== "undefined") {
      document.cookie = `${ANALYTICS_CONSENT_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`;
      document.cookie = `${ANALYTICS_CONSENT_EPOCH_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`;
    }
  }
  writeCookie(
    ANALYTICS_CONSENT_COOKIE,
    choice,
    60 * 60 * 24 * 365,
    configuredDomain,
  );
  writeCookie(
    ANALYTICS_CONSENT_EPOCH_COOKIE,
    epoch,
    60 * 60 * 24 * 365,
    configuredDomain,
  );
  writeSeenAnalyticsConsentEpoch(epoch);
  try {
    localStorage.setItem(ANALYTICS_CONSENT_SYNC_KEY, `${choice}:${epoch}`);
  } catch {
    // Cookie polling still propagates the choice when storage is unavailable.
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent<AnalyticsConsentSnapshot>(ANALYTICS_CONSENT_EVENT, {
        detail: { choice, epoch },
      }),
    );
  }
  return epoch;
}

function sameConsentSnapshot(
  first: AnalyticsConsentSnapshot | null,
  second: AnalyticsConsentSnapshot,
): boolean {
  return first?.choice === second.choice && first?.epoch === second.epoch;
}

function publishObservedConsent(): void {
  const snapshot = readAnalyticsConsentSnapshot();
  if (sameConsentSnapshot(lastObservedConsent, snapshot)) return;
  lastObservedConsent = snapshot;
  for (const observer of consentObservers) {
    observer(snapshot.choice, snapshot.epoch);
  }
}

function handleConsentStorage(event: StorageEvent): void {
  if (event.key === ANALYTICS_CONSENT_SYNC_KEY) publishObservedConsent();
}

/**
 * Observe the first-party cookie across tabs and sibling subdomains. Storage
 * gives same-origin tabs an immediate signal; bounded polling covers the
 * root-domain cookie because browser storage itself is origin-scoped.
 */
export function subscribeToAnalyticsConsent(
  observer: AnalyticsConsentObserver,
): () => void {
  if (typeof window === "undefined") return () => undefined;
  consentObservers.add(observer);
  if (consentObservers.size === 1) {
    lastObservedConsent = readAnalyticsConsentSnapshot();
    window.addEventListener(ANALYTICS_CONSENT_EVENT, publishObservedConsent);
    window.addEventListener("storage", handleConsentStorage);
    consentPoll = window.setInterval(
      publishObservedConsent,
      ANALYTICS_CONSENT_POLL_INTERVAL_MS,
    );
  }

  let subscribed = true;
  return () => {
    if (!subscribed) return;
    subscribed = false;
    consentObservers.delete(observer);
    if (consentObservers.size > 0) return;
    window.removeEventListener(ANALYTICS_CONSENT_EVENT, publishObservedConsent);
    window.removeEventListener("storage", handleConsentStorage);
    if (consentPoll !== null) window.clearInterval(consentPoll);
    consentPoll = null;
    lastObservedConsent = null;
  };
}

function browserStores(): Storage[] {
  const stores: Storage[] = [];
  try {
    if (typeof localStorage !== "undefined") stores.push(localStorage);
  } catch {
    // The root-domain consent cookie remains authoritative without storage.
  }
  try {
    if (typeof sessionStorage !== "undefined") stores.push(sessionStorage);
  } catch {
    // The root-domain consent cookie remains authoritative without storage.
  }
  return stores;
}

function clearDataFastStorage(): void {
  const stores = browserStores();
  for (const store of stores) {
    for (const name of DATAFAST_STORAGE_NAMES) {
      try {
        store.removeItem(name);
      } catch {
        // Continue clearing the other narrowly scoped DataFast values.
      }
    }
    try {
      store.removeItem("datafast_ignore");
    } catch {
      // Continue with the other browser store.
    }
  }
}

/** Resume an already-consented browser without discarding recoverable events. */
export function resumeDataFastBrowserStateForConsent(): void {
  for (const store of browserStores()) {
    try {
      store.removeItem("datafast_ignore");
    } catch {
      // The root-domain consent cookie remains authoritative without storage.
    }
  }
}

/** Purge pre-consent state before an explicit null/rejected -> accepted edge. */
export function prepareDataFastBrowserStateForConsent(
  configuredDomain: string,
): void {
  for (const name of DATAFAST_COOKIE_NAMES) {
    expireCookieEverywhere(name, configuredDomain);
  }
  clearDataFastStorage();
}

export function clearDataFastBrowserState(configuredDomain: string): void {
  for (const name of DATAFAST_COOKIE_NAMES) {
    expireCookieEverywhere(name, configuredDomain);
  }
  clearDataFastStorage();
  try {
    localStorage.setItem("datafast_ignore", "true");
  } catch {
    // The root-domain consent cookie remains authoritative across subdomains.
  }
}

export function readCookie(name: string): string | null {
  return cookieValue(name);
}
